// React and hooks
import { memo, useCallback, useEffect, useState } from "react";

// API calls and types
import { getTehnicalInspectionAPI } from "services/api/calculations/tehnicalinspection/api";
import { TehnicalInspectionDetailFormValues } from "services/api/calculations/tehnicalinspection/types";
import { initialTehnicalInspectionDetailForm } from "services/api/calculations/tehnicalinspection/consts";
import { TehnicalInspectionDetailProps } from "./types";

// UI components
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import { Loader } from "ui/components/Loader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Page-specific components
import TehnicalInspectionEditCard from "../../../TehnicalInspectionEditCard/TehnicalInspectionEditCard";

// Helpers and utilities
import { useTranslate } from "helpers";

// Constants
import { CALCULATION_TEHNICAL_INSPECTION_CREATE_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Errors } from "services/forms/validation/types";

const TehnicalInspectionDetailCard: React.FC<TehnicalInspectionDetailProps> = (
  props
) => {
  const { tehnicalInspectionId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<TehnicalInspectionDetailFormValues>(
      initialTehnicalInspectionDetailForm
    );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (tehnicalInspectionId) {
      fetchTehnicalInspection(tehnicalInspectionId);
    }
  }, [tehnicalInspectionId]);

  const fetchTehnicalInspection = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTehnicalInspectionDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTehnicalInspectionAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (tehnicalInspectionId) fetchTehnicalInspection(tehnicalInspectionId);
    setShowEditModal(false);
  }, []);

  if (!tehnicalInspectionId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.setting.tehnicalinspection.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t(
                  "admin.calculation.setting.tehnicalinspection.detail.vehiclegroupname"
                )}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.vehicleGroupName}
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.setting.tehnicalinspection.detail.price")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.price}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_TEHNICAL_INSPECTION_CREATE_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <TehnicalInspectionEditCard
              tehnicalInspectionId={tehnicalInspectionId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TehnicalInspectionDetailCard);
